import React, { useState, useEffect } from 'react';
import { Contact } from '../types/Contact';
import { User } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';
import { listContactsCore, listContactsForGroupCore, toggleArchiveContactCore, deleteContactCore } from '../lib/contactsCore';
import { Archive, Search, ArchiveRestore, Trash2, Check } from 'lucide-react';
import { useGroupContext } from '../lib/groups/context';

interface ArchivedContactsProps {
    onContactSelect: (contact: Contact) => void;
    user?: User | null;
}

export default function ArchivedContacts({ onContactSelect, user }: ArchivedContactsProps) {
    const [archivedContacts, setArchivedContacts] = useState<Contact[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const { activeGroupId } = useGroupContext();

    useEffect(() => {
        fetchArchivedContacts();
    }, [user?.id, activeGroupId]);

    const fetchArchivedContacts = async () => {
        setLoading(true);
        try {
            const q = (searchTerm || '').trim();
            const opts = { includeArchived: true as const, query: q, limit: 200 };
            const rows = activeGroupId
                ? await listContactsForGroupCore(supabase as any, activeGroupId, opts)
                : await listContactsCore(supabase as any, opts);
            // Already returns plain contacts when group-scoped; filter archived only to be safe
            setArchivedContacts((rows || []).filter((r: any) => r.is_archived));
        } catch (error) {
            console.error('Error fetching archived contacts:', error);
            alert('Failed to fetch archived contacts');
        } finally {
            setLoading(false);
        }
    };

    const handleUnarchive = async (contact: Contact, e: React.MouseEvent) => {
        e.stopPropagation(); // Prevent contact selection
        try {
            await toggleArchiveContactCore(supabase as any, String(contact.id), false);

            // Remove from archived list
            setArchivedContacts(prev => prev.filter(c => c.id !== contact.id));
            alert('Contact unarchived successfully');
        } catch (error) {
            console.error('Error unarchiving contact:', error);
            alert('Failed to unarchive contact. Please try again.');
        }
    };

    const handlePermanentDelete = async (contact: Contact, e: React.MouseEvent) => {
        e.stopPropagation(); // Prevent contact selection

        const confirmDelete = window.confirm(
            `Are you sure you want to permanently delete ${contact.first_name} ${contact.last_name}? This action cannot be undone.`
        );

        if (!confirmDelete) return;

        try {
            await deleteContactCore(supabase as any, String(contact.id));

            // Remove from archived list
            setArchivedContacts(prev => prev.filter(c => c.id !== contact.id));
            alert('Contact permanently deleted');
        } catch (error) {
            console.error('Error deleting contact:', error);
            alert('Failed to delete contact. Please try again.');
        }
    };

    const filteredContacts = archivedContacts.filter(contact => {
        const searchLower = searchTerm.toLowerCase();
        return (
            `${contact.first_name} ${contact.last_name}`.toLowerCase().includes(searchLower) ||
            (contact.company || '').toLowerCase().includes(searchLower) ||
            (contact.title || '').toLowerCase().includes(searchLower) ||
            (contact.email || '').toLowerCase().includes(searchLower)
        );
    });

    return (
        <div className="p-6 space-y-6">
            {/* Header */}
            <div className="bg-black border border-gray-800 p-6">
                <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center space-x-3">
                        <Archive className="w-6 h-6 text-yellow-400" />
                        <h2 className="text-xl font-light text-white tracking-wide">ARCHIVED CONTACTS</h2>
                    </div>
                    <span className="text-sm text-gray-400">{archivedContacts.length} archived</span>
                </div>
                <p className="text-gray-400 text-sm">
                    Archived contacts are hidden from your main contact list but can be restored at any time.
                </p>
            </div>

            {/* Search */}
            <div className="bg-black border border-gray-800 p-4">
                <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <input
                        type="text"
                        placeholder="Search"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                e.currentTarget.blur(); // Close keyboard on mobile
                            }
                        }}
                        className="w-full pl-10 pr-4 py-2 bg-gray-900/50 border border-gray-700/50 text-white placeholder-gray-500 focus:outline-none focus:border-gray-500 text-sm"
                    />
                </div>
            </div>

            {/* Contact List */}
            <div className="bg-black border border-gray-800">
                {loading ? (
                    <div className="p-8 text-center">
                        <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-white mb-4"></div>
                        <p className="text-gray-400">Loading archived contacts...</p>
                    </div>
                ) : filteredContacts.length === 0 ? (
                    <div className="p-8 text-center">
                        <Archive className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                        <p className="text-gray-400 text-lg">
                            {searchTerm ? 'No archived contacts match your search' : 'No archived contacts'}
                        </p>
                        <p className="text-gray-500 text-sm mt-2">
                            {searchTerm ? 'Try a different search term' : 'Archived contacts will appear here'}
                        </p>
                    </div>
                ) : (
                    <div className="divide-y divide-gray-800">
                        {filteredContacts.map((contact) => (
                            <div
                                key={contact.id}
                                onClick={() => onContactSelect(contact)}
                                className="p-4 hover:bg-gray-900/50 cursor-pointer transition-colors group"
                            >
                                <div className="flex items-center justify-between">
                                    <div className="flex-1">
                                        <div className="flex items-center space-x-3">
                                            {/* Profile Image or Initials with Status Overlay */}
                                            <div className="w-10 h-10 bg-gray-700 rounded-full flex items-center justify-center overflow-hidden flex-shrink-0 relative">
                                                {contact.profile_image_url ? (
                                                    <img
                                                        src={contact.profile_image_url}
                                                        alt=""
                                                        className="w-full h-full object-cover"
                                                    />
                                                ) : (
                                                    <span className="text-white text-sm font-medium">
                                                        {contact.first_name?.[0]}{contact.last_name?.[0]}
                                                    </span>
                                                )}

                                                {/* Green overlay for contacted status - REMOVED for basic contacts view */}
                                            </div>

                                            {/* Contact Info */}
                                            <div className="flex-1 min-w-0">
                                                <h3 className="text-white font-semibold tracking-wide">
                                                    {contact.first_name} {contact.last_name}
                                                </h3>
                                                <p className="text-gray-400 text-sm truncate">
                                                    {contact.title} {contact.title && contact.company && 'at'} {contact.company}
                                                </p>
                                                {contact.email && (
                                                    <p className="text-gray-500 text-xs truncate">{contact.email}</p>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Action Buttons */}
                                    <div className="flex items-center space-x-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                        <button
                                            onClick={(e) => handleUnarchive(contact, e)}
                                            className="p-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                                            title="Unarchive Contact"
                                        >
                                            <ArchiveRestore className="w-4 h-4" />
                                        </button>
                                        <button
                                            onClick={(e) => handlePermanentDelete(contact, e)}
                                            className="p-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                                            title="Permanently Delete Contact"
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
